import { Component, inject, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { hub } from '@src/app/app.const';
import { AppAuthService } from '@src/app/core/services/app-auth.service';
import { PasswordStrengthModel } from '@src/app/shared/components/password-strength/interfaces/password-strength.model';
import { Hub } from 'aws-amplify/utils';

@Component({
  selector: 'app-force-change-password-dialog',
  templateUrl: './force-change-password-dialog.component.html',
  styleUrls: ['./force-change-password-dialog.component.scss'],
  standalone: false,
})
export class ForceChangePasswordDialogComponent {
  passwordPolicies: any;

  passwordStrengthForm = signal<PasswordStrengthModel>({
    confirmPassword: '',
    password: '',
    valid: false,
  });

  private _userName: string;
  private _passwordExpired: boolean;

  private _dialogRef = inject(MatDialogRef<ForceChangePasswordDialogComponent>);
  private readonly _data = inject(MAT_DIALOG_DATA);
  private _authService = inject(AppAuthService);

  constructor() {
    this.passwordPolicies = this._data.passwordPolicies;

    this._userName = this._data.userName;
    this._passwordExpired = this._data.passwordExpired;

    this._changePasswordForm = new FormGroup({
      confirmationCode: new FormControl('', {
        validators: [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      }),
    });
  }

  getFormValues(event: PasswordStrengthModel) {
    this.passwordStrengthForm.set(event);
  }

  private _changePasswordForm: FormGroup;

  get changePasswordForm(): FormGroup {
    return this._changePasswordForm;
  }

  get userName(): string {
    return this._userName ?? '';
  }

  get confirmationCode(): AbstractControl {
    return this.changePasswordForm.controls.confirmationCode;
  }

  get passwordExpired(): boolean {
    return this._passwordExpired;
  }

  public async onSave(): Promise<void> {
    try {
      await this._authService.confirmResetPassword(
        this.userName,
        this.confirmationCode.value.trim(),
        this.passwordStrengthForm().password
      );

      this._dialogRef.close(true);
    } catch (error) {
      switch (error.code) {
        case 'LimitExceededException':
        case 'CodeMismatchException':
          Hub.dispatch(hub.channel.app.name, {
            event: hub.channel.app.event.error,
            data: {
              title: error.message,
            },
          });
          break;
        case 'ExpiredCodeException':
          Hub.dispatch(hub.channel.app.name, {
            event: hub.channel.app.event.error,
            data: {
              title: error.message,
              message:
                'The 6-digit Verification Code entered has expired. Please click on the Resend Code button, and enter the new code that will be sent to your email.',
            },
          });
          break;
        default:
          Hub.dispatch(hub.channel.app.name, {
            event: hub.channel.app.event.error,
          });
          break;
      }
    }
  }

  async onResendConfirmationCode(): Promise<void> {
    try {
      const output = await this._authService.resetPassword(this.userName);

      switch (output.nextStep.resetPasswordStep) {
        case 'CONFIRM_RESET_PASSWORD_WITH_CODE': {
          Hub.dispatch(hub.channel.app.name, {
            event: hub.channel.app.event.success,
            message: `A new verification code has been sent to you email account.`,
          });
          break;
        }
      }
    } catch (error: any) {
      if (error && error.code === 'LimitExceededException') {
        Hub.dispatch(hub.channel.app.name, {
          event: hub.channel.app.event.error,
          data: {
            title: error.message,
          },
        });
      } else {
        Hub.dispatch(hub.channel.app.name, {
          event: hub.channel.app.event.error,
        });
      }
    }
  }
}
